import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Award,
  Calendar,
  Clock,
  BarChart3,
  CheckCircle2,
  XCircle,
  Brain,
  Code2,
  Building2,
  Database,
  Terminal,
  Zap,
  AlertTriangle,
  Lightbulb,
  Check,
  X,
  FileCode,
  Layers,
  Cpu,
  Mail,
  UserCheck,
  Sparkles,
  TrendingUp,
  BookOpen,
  Briefcase,
  AlertCircle,
  Globe,
} from "lucide-react";
import AssessmentClient from "../data/api/AssessmentClient";
import Loader from "../../../shared/components/Loader";
import CodeSyntaxHighlighter from "../../../shared/components/CodeSyntaxHighlighter";
import "../styles/AssessmentResultPage.css";

interface SkillScore {
  skill_name: string;
  score: number;
  correct?: number;
  total?: number;
}

interface DimensionScore {
  dimension_id: string;
  dimension_name: string;
  dimension_score: number;
  skill_score: SkillScore[];
}

interface ProgrammingReport {
  question_id: string;
  problem_statement: string;
  submitted_code: string;
  programming_language: string;
  is_correct: boolean;
  score: number;
  analysis?: {
    recommended_approach?: string;
    time_complexity?: string;
    space_complexity?: string;
    edge_cases_identified?: string[];
    best_practices_violations?: string[];
    code_quality_score?: number | string;
    correctness_score?: number | string;
    test_cases_analysis?: string;
    code_review_feedback?: string;
    positive_aspects?: string[];
    optimization_suggestions?: string[];
  };
  execution_result?: {
    status?: string;
    test_cases_passed?: number | string;
    test_cases_failed?: number | string;
    test_case_results?: Array<{
      test_case_id?: string;
      input?: string;
      expected_output?: string;
      actual_output?: string;
      passed?: boolean;
      error_message?: string;
    }>;
  };
}

interface IndustryOption {
  id?: string;
  text: string;
}

interface IndustryReport {
  question_id: string;
  question: string;
  selected_answer?: string;
  correct_answer?: string;
  is_correct: boolean;
  options: (string | IndustryOption)[];
}

interface SqlReport {
  question_id?: string;
  problem_statement?: string;
  submitted_query?: string;
  is_correct?: boolean;
  score?: number;
  analysis?: {
    first_mismatch_row?: string | number;
    is_correct?: boolean;
    join_check_passed?: boolean;
    order_check_passed?: boolean;
    index_check_passed?: boolean;
    used_index?: boolean;
    full_table_scan?: boolean;
    feedback_summary?: string;
    performance_ratio?: string | number;
    extra_columns?: string[];
    missing_columns?: string[];
    expected_row_count?: string | number;
    actual_row_count?: string | number;
    candidate_execution_time_ms?: string | number;
    reference_execution_time_ms?: string | number;
    correctness_score?: number | string;
    performance_score?: number | string;
    final_score?: number | string;
    suggestions?: string[];
  };
  execution_result?: {
    row_count?: string | number;
    status?: string;
    submitted_query?: string;
    rows?: Record<string, any>[];
  };
}

interface ResultData {
  assessment_entity_id?: string;
  candidate_id?: string;
  email_address?: string;
  first_name?: string;
  last_name?: string;
  attended?: boolean;
  status?: string;
  score?: number | string;
  jozuna_score?: number | string;
  programming_score?: number | string;
  industry_score?: number | string;
  sql_score?: number | string;
  correct_answers?: number;
  total_questions?: number;
  attempted_questions?: number;
  submitted_at?: string;
  overall_score?: number | string;
  overall_percentage?: number | string;
  assessment_name?: string;
  company_name?: string;
  skill_report?: DimensionScore[];
  programming_report?: ProgrammingReport[];
  industry_report?: IndustryReport[];
  sql_report?: SqlReport[];
}

const AssessmentCandidateResultPage: React.FC = () => {
  const navigate = useNavigate();
  const { assessmentId, candidateId } = useParams<{
    assessmentId: string;
    candidateId: string;
  }>();

  const [loading, setLoading] = useState<boolean>(true);
  const [result, setResult] = useState<ResultData | null>(null);
  const [error, setError] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("");

  useEffect(() => {
    if (assessmentId && candidateId) {
      const fetchResults = async () => {
        setLoading(true);
        setError("");
        try {
          const rawData =
            await AssessmentClient.getAssessmentResults(assessmentId);
          // Extract specific candidate from assessment results records
          let candidateRecord = null;
          if (rawData && Array.isArray(rawData.records)) {
            candidateRecord =
              rawData.records.find(
                (r: any) => r.candidate_id === candidateId,
              ) || rawData.records[0];
          } else {
            candidateRecord = rawData;
          }

          setResult((candidateRecord as ResultData) || null);
        } catch (e: any) {
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "Failed to load detailed candidate results.",
          );
        } finally {
          setLoading(false);
        }
      };
      fetchResults();
    }
  }, [assessmentId, candidateId]);

  // Determine available tabs out of the 4 question types
  const hasJozuna = Boolean(
    result?.skill_report &&
    Array.isArray(result.skill_report) &&
    result.skill_report.length > 0,
  );
  const hasProgramming = Boolean(
    result?.programming_report &&
    Array.isArray(result.programming_report) &&
    result.programming_report.length > 0,
  );
  const hasIndustry = Boolean(
    result?.industry_report &&
    Array.isArray(result.industry_report) &&
    result.industry_report.length > 0,
  );
  const hasSql = Boolean(
    result?.sql_report &&
    Array.isArray(result.sql_report) &&
    result.sql_report.length > 0,
  );

  // Helper score calculators
  const getJozunaScore = () => {
    if (
      result?.jozuna_score !== undefined &&
      result?.jozuna_score !== "NA" &&
      result?.jozuna_score !== null
    ) {
      return Math.round(Number(result.jozuna_score));
    }
    if (hasJozuna) {
      let totalSkills = 0;
      let sumScore = 0;
      result!.skill_report!.forEach((dim) => {
        if (!dim) return;
        if (Array.isArray(dim.skill_score) && dim.skill_score.length > 0) {
          dim.skill_score.forEach((s) => {
            if (!s) return;
            sumScore += Number(s.score || 0);
            totalSkills++;
          });
        } else if (dim.dimension_score !== undefined) {
          sumScore += Number(dim.dimension_score);
          totalSkills++;
        }
      });
      return totalSkills > 0 ? Math.round(sumScore / totalSkills) : "N/A";
    }
    return "N/A";
  };

  const getProgrammingScore = () => {
    if (
      result?.programming_score !== undefined &&
      result?.programming_score !== "NA" &&
      result?.programming_score !== null
    ) {
      return Math.round(Number(result.programming_score));
    }
    if (hasProgramming) {
      const sum = result!.programming_report!.reduce(
        (acc, curr) => acc + (Number(curr.score) || 0),
        0,
      );
      return Math.round(sum / result!.programming_report!.length);
    }
    return "N/A";
  };

  const getIndustryScore = () => {
    if (
      result?.industry_score !== undefined &&
      result?.industry_score !== "NA" &&
      result?.industry_score !== null
    ) {
      return Math.round(Number(result.industry_score));
    }
    if (hasIndustry) {
      const correctCount = result!.industry_report!.filter(
        (r) => r.is_correct,
      ).length;
      return Math.round((correctCount / result!.industry_report!.length) * 100);
    }
    return "N/A";
  };

  const getSqlScore = () => {
    if (
      result?.sql_score !== undefined &&
      result?.sql_score !== "NA" &&
      result?.sql_score !== null
    ) {
      return Math.round(Number(result.sql_score));
    }
    if (hasSql) {
      const sum = result!.sql_report!.reduce((acc, curr) => {
        const s =
          curr.score ??
          curr.analysis?.final_score ??
          curr.analysis?.performance_score ??
          0;
        return acc + Number(s);
      }, 0);
      return Math.round(sum / result!.sql_report!.length);
    }
    return "N/A";
  };

  const jozunaScoreVal = getJozunaScore();
  const programmingScoreVal = getProgrammingScore();
  const industryScoreVal = getIndustryScore();
  const sqlScoreVal = getSqlScore();

  // Tab Definitions
  const allTabs = [
    {
      id: "jozuna",
      label: "JozHRM Soft Skills",
      icon: Brain,
      hasData: hasJozuna,
      score:
        jozunaScoreVal !== "N/A"
          ? `${jozunaScoreVal}%`
          : `${result?.skill_report?.length || 0} Dimensions`,
      count: result?.skill_report?.length || 0,
    },
    {
      id: "industry",
      label: "Industry",
      icon: Building2,
      hasData: hasIndustry,
      score:
        industryScoreVal !== "N/A"
          ? `${industryScoreVal}%`
          : `${result?.industry_report?.length || 0} Questions`,
      count: result?.industry_report?.length || 0,
    },
    {
      id: "programming",
      label: "Programming",
      icon: Code2,
      hasData: hasProgramming,
      score:
        programmingScoreVal !== "N/A"
          ? `${programmingScoreVal}%`
          : `${result?.programming_report?.length || 0} Problems`,
      count: result?.programming_report?.length || 0,
    },
    {
      id: "sql",
      label: "SQL",
      icon: Database,
      hasData: hasSql,
      score:
        sqlScoreVal !== "N/A"
          ? `${sqlScoreVal}%`
          : `${result?.sql_report?.length || 0} Queries`,
      count: result?.sql_report?.length || 0,
    },
  ];

  // ONLY keep tabs that have data (e.g. if industry_report is [], Industry tab is filtered out!)
  const visibleTabs = allTabs.filter((tab) => tab.hasData);

  // Automatically select the 1st available tab
  useEffect(() => {
    if (visibleTabs.length > 0) {
      if (!activeTab || !visibleTabs.some((t) => t.id === activeTab)) {
        setActiveTab(visibleTabs[0].id);
      }
    }
  }, [visibleTabs, activeTab]);

  const candidateName =
    [result?.first_name, result?.last_name].filter(Boolean).join(" ") ||
    result?.email_address ||
    "Candidate Result";
  const overallPct =
    typeof result?.score === "number"
      ? Math.round(result.score)
      : typeof result?.overall_score === "number"
        ? Math.round(result.overall_score)
        : 0;

  const formatSubmittedTime = (raw?: string) => {
    if (!raw) return "N/A";
    try {
      const iso = raw.endsWith("Z") || raw.includes("+") ? raw : raw + "Z";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return raw;
      return d.toLocaleString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return raw;
    }
  };

  // Valid Jozuna dimensions (including college if present)
  const validDimensions = (result?.skill_report || []).filter((dim) => {
    if (!dim) return false;
    if (
      (!dim.skill_score || dim.skill_score.length === 0) &&
      (!dim.dimension_score || dim.dimension_score === 0)
    )
      return false;
    return true;
  }).map(dim => ({
    ...dim,
    dimension_name: dim.dimension_name === 'School' ? 'Information Technology' : dim.dimension_name
  }));

  // Calculate top performing dimension
  const topDimension =
    validDimensions.length > 0
      ? [...validDimensions].sort(
          (a, b) => (b.dimension_score || 0) - (a.dimension_score || 0),
        )[0]
      : null;

  // Count total evaluated skills
  const totalSkillsCount = validDimensions.reduce(
    (acc, dim) => acc + (dim.skill_score?.length || 0),
    0,
  );

  const getScoreStatus = (pct: number) => {
    if (pct >= 80) return "High Proficiency";
    if (pct >= 60) return "Good Performance";
    if (pct >= 40) return "Moderate Pass";
    return "Needs Improvement";
  };

  const getDimensionIcon = (name?: string) => {
    const n = (name || "").toLowerCase();
    if (n.includes("learning") || n.includes("innovation")) return Brain;
    if (n.includes("interdisciplinary") || n.includes("global")) return Globe;
    if (n.includes("school") || n.includes("academic")) return BookOpen;
    if (n.includes("life") || n.includes("career")) return Briefcase;
    return Sparkles;
  };

  const getLangExt = (lang?: string) => {
    const l = (lang || "").toLowerCase();
    if (l.includes("python")) return "py";
    if (l.includes("javascript") || l.includes("js")) return "js";
    if (l.includes("typescript") || l.includes("ts")) return "ts";
    if (l.includes("java")) return "java";
    if (l.includes("c++") || l.includes("cpp")) return "cpp";
    if (l.includes("c#")) return "cs";
    if (l.includes("go")) return "go";
    if (l.includes("sql")) return "sql";
    return "code";
  };

  return (
    <div className="cand-res-container">
      <Loader
        isLoading={loading}
        message="Fetching detailed candidate results..."
      />

      {error && !loading && (
        <div className="cand-res-empty-box">
          <AlertTriangle
            size={40}
            color="#f59e0b"
            style={{ margin: "0 auto 12px auto" }}
          />
          <h3>Detailed Results Unavailable</h3>
          <p>{error}</p>
        </div>
      )}

      {result && !loading && (
        <>
          {/* Executive Header Bar */}
          <div className="cand-res-header-bar">
            <div className="cand-res-header-left">
              <button
                className="cand-res-back-btn-compact"
                onClick={() => navigate(-1)}
              >
                <ChevronLeft size={16} />
              </button>
              <div className="cand-res-cand-info">
                <h1 className="cand-res-cand-name">{candidateName}</h1>
                <div className="cand-res-cand-email">
                  <Mail size={13} />{" "}
                  {result.email_address || "No email provided"}
                </div>
              </div>
            </div>

            <div className="cand-res-header-right">
              <div className="cand-res-sub-chip">
                <Clock size={16} color="#a7f3d0" />
                <div>
                  <div className="cand-res-sub-val">
                    {formatSubmittedTime(result.submitted_at)}
                  </div>
                  <div className="cand-res-sub-lbl">Submitted At</div>
                </div>
              </div>

              {/* High-Tech Radial Glowing Score Dial Gauge */}
              <div className="cand-res-dial-widget">
                <div className="cand-res-dial-svg-wrap">
                  <svg viewBox="0 0 52 52">
                    <circle
                      className="cand-res-dial-track"
                      cx="26"
                      cy="26"
                      r="21"
                    />
                    <circle
                      className="cand-res-dial-fill"
                      cx="26"
                      cy="26"
                      r="21"
                      style={{
                        strokeDasharray: "131.95",
                        strokeDashoffset: `${131.95 - (overallPct / 100) * 131.95}`,
                      }}
                    />
                  </svg>
                  <div className="cand-res-dial-center">{overallPct}%</div>
                </div>
                <div className="cand-res-dial-meta">
                  <span className="cand-res-dial-title">Overall Score</span>
                </div>
              </div>
            </div>
          </div>

          {/* Top 4 Buttons / Tabs */}
          {visibleTabs.length > 0 ? (
            <div className="cand-res-tabs-bar">
              {visibleTabs.map((tab) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    className={`cand-res-tab-btn ${isActive ? "active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <IconComponent size={16} />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="cand-res-empty-box">
              <Brain
                size={40}
                color="#cbd5e1"
                style={{ margin: "0 auto 12px auto" }}
              />
              <h3>No Section Reports Found</h3>
              <p>
                This candidate does not have detailed question responses
                recorded.
              </p>
            </div>
          )}

          {/* Active Tab Content Area - Scrollable Inside Card Container */}
          <div className="cand-res-tab-content-container">
            {/* TAB 1: JOZUNA REPORT */}
            {activeTab === "jozuna" && hasJozuna && (
              <div>
                {/* Jozuna Overview Strip */}
                <div className="cand-res-overview-strip">
                  <div className="cand-res-overview-card">
                    <div
                      className="cand-res-overview-icon"
                      style={{ background: "#e6f4f1", color: "#0d6e5b" }}
                    >
                      <Brain size={22} />
                    </div>
                    <div className="cand-res-overview-info">
                      <span className="cand-res-overview-lbl">
                        JozHRM Soft Skills Score
                      </span>
                      <span className="cand-res-overview-val">
                        {jozunaScoreVal !== "N/A"
                          ? `${jozunaScoreVal}%`
                          : "N/A"}
                      </span>
                      <span className="cand-res-overview-sub">
                        Overall Dimension Average
                      </span>
                    </div>
                  </div>

                  <div className="cand-res-overview-card">
                    <div
                      className="cand-res-overview-icon"
                      style={{ background: "#fef3c7", color: "#b45309" }}
                    >
                      <TrendingUp size={22} />
                    </div>
                    <div className="cand-res-overview-info">
                      <span className="cand-res-overview-lbl">
                        Top Strength
                      </span>
                      <span
                        className="cand-res-overview-val"
                        style={{ fontSize: "15px" }}
                      >
                        {topDimension ? topDimension.dimension_name : "N/A"}
                      </span>
                      <span className="cand-res-overview-sub">
                        {topDimension
                          ? `${Math.round(topDimension.dimension_score)}% Score`
                          : "No dimension data"}
                      </span>
                    </div>
                  </div>

                  <div className="cand-res-overview-card">
                    <div
                      className="cand-res-overview-icon"
                      style={{ background: "#e0e7ff", color: "#3730a3" }}
                    >
                      <Sparkles size={22} />
                    </div>
                    <div className="cand-res-overview-info">
                      <span className="cand-res-overview-lbl">
                        Skills Evaluated
                      </span>
                      <span className="cand-res-overview-val">
                        {totalSkillsCount} Skills
                      </span>
                      <span className="cand-res-overview-sub">
                        {validDimensions.length} Core Dimensions
                      </span>
                    </div>
                  </div>
                </div>

                <div className="cand-res-section-title">
                  <h2>
                    <Brain size={20} color="var(--iq-primary, #0d6e5b)" />{" "}
                    JozHRM Soft Skills & Dimension Analysis
                  </h2>
                  <span className="cand-res-score-badge">
                    Overall JozHRM Soft Skills Score:{" "}
                    {jozunaScoreVal !== "N/A" ? `${jozunaScoreVal}%` : "N/A"}
                  </span>
                </div>

                <div className="cand-res-jozuna-grid">
                  {validDimensions.map((dim, idx) => {
                    const dimPct = Math.round(dim.dimension_score || 0);
                    const DimIcon = getDimensionIcon(dim.dimension_name);

                    return (
                      <div
                        key={dim.dimension_id || idx}
                        className="cand-res-dim-card"
                      >
                        <div className="cand-res-dim-header">
                          <div className="cand-res-dim-header-left">
                            <div className="cand-res-dim-icon-badge">
                              <DimIcon size={18} />
                            </div>
                            <h3>{dim.dimension_name || "Dimension"}</h3>
                          </div>
                          <span className="cand-res-dim-score">{dimPct}%</span>
                        </div>

                        <div className="cand-res-dim-body">
                          {Array.isArray(dim.skill_score) &&
                          dim.skill_score.length > 0 ? (
                            dim.skill_score.map((sk, sIdx) => {
                              if (!sk) return null;
                              const skPct = Math.round(sk.score || 0);
                              let barColor = "#ef4444";
                              let chipBg = "#fef2f2";
                              let chipColor = "#991b1b";
                              let StatusIcon = AlertCircle;
                              let statusText = "Needs Focus";

                              if (skPct >= 75) {
                                barColor = "#10b981";
                                chipBg = "#f0fdf4";
                                chipColor = "#15803d";
                                StatusIcon = CheckCircle2;
                                statusText = "Proficient";
                              } else if (skPct >= 50) {
                                barColor = "#f59e0b";
                                chipBg = "#fffbeb";
                                chipColor = "#b45309";
                                StatusIcon = Sparkles;
                                statusText = "Moderate";
                              }

                              return (
                                <div key={sIdx} className="cand-res-skill-item">
                                  <div className="cand-res-skill-info">
                                    <div className="cand-res-skill-left">
                                      <StatusIcon size={14} color={barColor} />
                                      <span className="cand-res-skill-name">
                                        {sk.skill_name || "Skill"}
                                      </span>
                                    </div>
                                    <span
                                      className="cand-res-skill-badge"
                                      style={{
                                        backgroundColor: chipBg,
                                        color: chipColor,
                                      }}
                                    >
                                      {skPct}% • {statusText}
                                    </span>
                                  </div>
                                  <div className="cand-res-progress-track">
                                    <div
                                      className="cand-res-progress-fill"
                                      style={{
                                        width: `${skPct}%`,
                                        background: `linear-gradient(90deg, ${barColor} 0%, ${barColor}dd 100%)`,
                                      }}
                                    />
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p
                              style={{
                                color: "#94a3b8",
                                fontSize: "13px",
                                margin: 0,
                              }}
                            >
                              No skills listed under this dimension.
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB 2: INDUSTRY REPORT */}
            {activeTab === "industry" && hasIndustry && (
              <div>
                <div className="cand-res-section-title">
                  <h2>
                    <Building2 size={22} color="#0d6e5b" /> Industry Assessment
                    Questions
                  </h2>
                  <span className="cand-res-score-badge">
                    Industry Score:{" "}
                    {industryScoreVal !== "N/A"
                      ? `${industryScoreVal}%`
                      : "N/A"}
                  </span>
                </div>

                {result!.industry_report!.map((ind, i) => (
                  <div
                    key={ind.question_id || i}
                    className="cand-res-report-card"
                  >
                    <div className="cand-res-card-top-title">
                      <div className="cand-res-card-title-left">
                        <span className="cand-res-q-badge">Q{i + 1}</span>
                        <h3>Question {i + 1}</h3>
                      </div>
                    </div>

                    <div className="cand-res-report-body">
                      <div className="cand-res-problem-box">
                        <div className="cand-res-problem-lbl">Question</div>
                        <div className="cand-res-problem-text">
                          {ind.question}
                        </div>
                      </div>

                      <div style={{ marginTop: "16px" }}>
                        <div className="cand-res-problem-lbl">
                          Options & Answers
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "10px",
                            marginTop: "8px",
                          }}
                        >
                          {Array.isArray(ind.options) &&
                            ind.options.map((opt, optIdx) => {
                              if (!opt) return null;
                              const optionText =
                                typeof opt === "string"
                                  ? opt
                                  : String(opt?.text || "");
                              const isUserSelected =
                                ind.selected_answer === optionText;
                              const isCorrectChoice =
                                ind.correct_answer === optionText;
                              const letter = String.fromCharCode(65 + optIdx);

                              let optionStyle: React.CSSProperties = {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "12px 16px",
                                borderRadius: "10px",
                                border: "1px solid #e2e8f0",
                                background: "#ffffff",
                                fontSize: "14px",
                                color: "#334155",
                                transition: "all 0.2s ease",
                              };

                              if (isCorrectChoice) {
                                optionStyle = {
                                  ...optionStyle,
                                  background: "#f0fdf4",
                                  borderColor: "#bbf7d0",
                                  color: "#166534",
                                  fontWeight: 600,
                                };
                              } else if (isUserSelected && !isCorrectChoice) {
                                optionStyle = {
                                  ...optionStyle,
                                  background: "#fef2f2",
                                  borderColor: "#fca5a5",
                                  color: "#991b1b",
                                  fontWeight: 600,
                                };
                              }

                              return (
                                <div key={optIdx} style={optionStyle}>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "10px",
                                    }}
                                  >
                                    <span
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                        background: isCorrectChoice
                                          ? "#22c55e"
                                          : isUserSelected
                                            ? "#ef4444"
                                            : "#e2e8f0",
                                        color:
                                          isCorrectChoice || isUserSelected
                                            ? "#ffffff"
                                            : "#64748b",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                        flexShrink: 0,
                                      }}
                                    >
                                      {letter}
                                    </span>
                                    <span>{optionText}</span>
                                  </div>

                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "8px",
                                      alignItems: "center",
                                    }}
                                  >
                                    {isUserSelected && (
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          padding: "3px 8px",
                                          borderRadius: "6px",
                                          background: isCorrectChoice
                                            ? "#dcfce7"
                                            : "#fee2e2",
                                          color: isCorrectChoice
                                            ? "#15803d"
                                            : "#991b1b",
                                        }}
                                      >
                                        Your Choice{" "}
                                        {isCorrectChoice ? "✓" : "✗"}
                                      </span>
                                    )}
                                    {isCorrectChoice && !isUserSelected && (
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          padding: "3px 8px",
                                          borderRadius: "6px",
                                          background: "#166534",
                                          color: "#ffffff",
                                        }}
                                      >
                                        Correct Answer ✓
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* TAB 3: PROGRAMMING REPORT */}
            {activeTab === "programming" && hasProgramming && (
              <div>
                <div className="cand-res-section-title">
                  <h2>
                    <Code2 size={22} color="#0d6e5b" /> Programming Evaluation &
                    AI Analysis
                  </h2>
                  <span className="cand-res-score-badge">
                    Programming Score:{" "}
                    {programmingScoreVal !== "N/A"
                      ? `${programmingScoreVal}%`
                      : "N/A"}
                  </span>
                </div>

                {result!.programming_report!.map((prog, i) => {
                  const cleanedApproach = String(
                    prog.analysis?.recommended_approach || "",
                  )
                    .replace(/```[a-z]*\n?/gi, "")
                    .replace(/```/g, "")
                    .trim();

                  return (
                    <div
                      key={prog.question_id || i}
                      className="cand-res-report-card"
                    >
                      <div className="cand-res-card-top-title">
                        <div className="cand-res-card-title-left">
                          <span className="cand-res-q-badge prog">
                            P{i + 1}
                          </span>
                          <h3>
                            Problem {i + 1}{" "}
                            <span className="cand-res-lang-tag">
                              {prog.programming_language || "Code"}
                            </span>
                          </h3>
                        </div>
                        {prog.score !== undefined && (
                          <span className="cand-res-card-score-pill">
                            Score: {prog.score}%
                          </span>
                        )}
                      </div>

                      <div className="cand-res-report-body">
                        {/* Problem Statement */}
                        <div className="cand-res-problem-box">
                          <div className="cand-res-problem-lbl">
                            Problem Statement
                          </div>
                          <div className="cand-res-problem-text">
                            {String(prog.problem_statement || "").replace(
                              /\\n/g,
                              "\n",
                            )}
                          </div>
                        </div>

                        {/* Code Run Container / IDE Editor */}
                        <div className="cand-res-code-editor">
                          <div className="cand-res-code-editor-header">
                            <div className="cand-res-code-dots">
                              <span className="dot red" />
                              <span className="dot yellow" />
                              <span className="dot green" />
                            </div>
                            <div className="cand-res-code-title">
                              <FileCode size={13} />
                              <span>
                                solution.{getLangExt(prog.programming_language)}{" "}
                                — Submitted Code
                              </span>
                            </div>
                            <div className="cand-res-code-lang-pill">
                              {(
                                prog.programming_language || "CODE"
                              ).toUpperCase()}
                            </div>
                          </div>
                          <div className="cand-res-code-editor-body">
                            <pre className="cand-res-code-editor-block">
                              <CodeSyntaxHighlighter
                                code={
                                  prog.submitted_code || "// No code submitted"
                                }
                                language={prog.programming_language || "python"}
                              />
                            </pre>
                          </div>
                        </div>

                        {/* AI Review Analysis Box */}
                        {prog.analysis && (
                          <div className="cand-res-ai-card">
                            <div className="cand-res-ai-header">
                              <Lightbulb size={18} color="#0d6e5b" /> AI
                              Automated Code Review
                            </div>

                            <div className="cand-res-ai-metrics">
                              {prog.analysis.code_quality_score && (
                                <div className="cand-res-ai-metric-pill quality">
                                  <Zap size={14} /> Code Quality:{" "}
                                  {prog.analysis.code_quality_score}%
                                </div>
                              )}
                            </div>

                            {cleanedApproach && (
                              <div className="cand-res-approach-box">
                                <strong
                                  style={{
                                    color: "#0d6e5b",
                                    display: "block",
                                    marginBottom: "4px",
                                  }}
                                >
                                  Recommended Approach:
                                </strong>
                                {cleanedApproach}
                              </div>
                            )}

                            {prog.analysis.edge_cases_identified &&
                              prog.analysis.edge_cases_identified.length >
                                0 && (
                                <div style={{ marginTop: "12px" }}>
                                  <strong
                                    style={{
                                      fontSize: "13px",
                                      color: "#0d6e5b",
                                    }}
                                  >
                                    Edge Cases Identified:
                                  </strong>
                                  <ul className="cand-res-ai-bullets">
                                    {prog.analysis.edge_cases_identified.map(
                                      (e, idx) => (
                                        <li key={idx}>{e}</li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                          </div>
                        )}

                        {/* Test Cases Results */}
                        {prog.execution_result &&
                          Array.isArray(
                            prog.execution_result.test_case_results,
                          ) &&
                          prog.execution_result.test_case_results.length >
                            0 && (
                            <div className="cand-res-tc-section">
                              <div className="cand-res-tc-title">
                                <Zap size={16} color="#0d6e5b" /> Test Cases
                                Analysis (
                                {prog.execution_result.test_cases_passed || 0}/
                                {prog.execution_result.test_case_results.length}{" "}
                                Passed)
                              </div>
                              <div className="cand-res-tc-grid">
                                {prog.execution_result.test_case_results.map(
                                  (tc, tcIdx) => {
                                    if (!tc) return null;
                                    return (
                                      <div
                                        key={tc.test_case_id || tcIdx}
                                        className={`cand-res-tc-card ${tc.passed ? "passed" : "failed"}`}
                                      >
                                        <div className="cand-res-tc-head">
                                          <span>Test Case #{tcIdx + 1}</span>
                                          <span
                                            style={{
                                              color: tc.passed
                                                ? "#15803d"
                                                : "#b91c1c",
                                            }}
                                          >
                                            {tc.passed
                                              ? "PASSED ✓"
                                              : "FAILED ✗"}
                                          </span>
                                        </div>
                                        <div className="cand-res-tc-detail">
                                          <div>
                                            <strong>Input:</strong>{" "}
                                            {String(tc.input || "-")}
                                          </div>
                                          <div>
                                            <strong>Expected:</strong>{" "}
                                            {String(tc.expected_output || "-")}
                                          </div>
                                          <div>
                                            <strong>Actual:</strong>{" "}
                                            {String(tc.actual_output || "-")}
                                          </div>
                                          {tc.error_message && (
                                            <div
                                              style={{
                                                color: "#dc2626",
                                                marginTop: "4px",
                                              }}
                                            >
                                              <strong>Error:</strong>{" "}
                                              {String(tc.error_message)}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  },
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* TAB 4: SQL REPORT */}
            {activeTab === "sql" && hasSql && (
              <div>
                <div className="cand-res-section-title">
                  <h2>
                    <Database size={22} color="#0d6e5b" /> SQL Query Analysis &
                    Performance
                  </h2>
                  <span className="cand-res-score-badge">
                    SQL Score:{" "}
                    {sqlScoreVal !== "N/A" ? `${sqlScoreVal}%` : "N/A"}
                  </span>
                </div>

                {result!.sql_report!.map((sqlr, i) => {
                  const score =
                    sqlr.score ??
                    sqlr.analysis?.final_score ??
                    sqlr.analysis?.performance_score ??
                    0;
                  const problemStatement =
                    sqlr.problem_statement || "Database Query Challenge";
                  const submittedQuery =
                    sqlr.submitted_query ||
                    sqlr.execution_result?.submitted_query ||
                    "No query submitted";
                  const analysis = sqlr.analysis;

                  return (
                    <div
                      key={sqlr.question_id || i}
                      className="cand-res-report-card"
                    >
                      <div className="cand-res-card-top-title">
                        <div className="cand-res-card-title-left">
                          <span className="cand-res-q-badge sql">
                            SQL {i + 1}
                          </span>
                          <h3>SQL Task {i + 1}</h3>
                        </div>
                        {score !== undefined && (
                          <span className="cand-res-card-score-pill">
                            Score: {score}%
                          </span>
                        )}
                      </div>

                      <div className="cand-res-report-body">
                        {/* Task Description */}
                        <div className="cand-res-problem-box">
                          <div className="cand-res-problem-lbl">
                            Task Description
                          </div>
                          <div className="cand-res-problem-text">
                            {String(problemStatement || "").replace(
                              /\\n/g,
                              "\n",
                            )}
                          </div>
                        </div>

                        {/* Submitted SQL Block inside Code Run Container */}
                        <div className="cand-res-code-editor sql-theme">
                          <div className="cand-res-code-editor-header">
                            <div className="cand-res-code-dots">
                              <span className="dot red" />
                              <span className="dot yellow" />
                              <span className="dot green" />
                            </div>
                            <div className="cand-res-code-title">
                              <Database size={13} />
                              <span>query.sql — Submitted SQL Query</span>
                            </div>
                            <div className="cand-res-code-lang-pill sql">
                              SQL
                            </div>
                          </div>
                          <div className="cand-res-code-editor-body">
                            <pre className="cand-res-code-editor-block sql">
                              <CodeSyntaxHighlighter
                                code={submittedQuery}
                                language="sql"
                              />
                            </pre>
                          </div>
                        </div>

                        {/* Execution Performance & Metrics Bar */}
                        {analysis && (
                          <div
                            style={{ marginTop: "12px", marginBottom: "16px" }}
                          >
                            {analysis.candidate_execution_time_ms && (
                              <div className="cand-res-sql-perf-strip">
                                <div className="cand-res-sql-perf-item">
                                  <Clock
                                    size={15}
                                    className="cand-res-perf-icon"
                                  />
                                  <span className="cand-res-perf-lbl">
                                    Execution Time:
                                  </span>
                                  <span className="cand-res-perf-val">
                                    {analysis.candidate_execution_time_ms} ms
                                  </span>
                                  {/* {analysis.reference_execution_time_ms && (
                                    <span className="cand-res-perf-ref">
                                      (Ref: {analysis.reference_execution_time_ms} ms)
                                    </span>
                                  )} */}
                                </div>
                              </div>
                            )}

                            {/* Suggestions and Feedback */}
                            {analysis.suggestions &&
                              analysis.suggestions.length > 0 && (
                                <div
                                  className="cand-res-ai-card"
                                  style={{
                                    background: "#fff7ed",
                                    borderColor: "#ffedd5",
                                    marginTop: "12px",
                                  }}
                                >
                                  <div
                                    className="cand-res-ai-header"
                                    style={{ color: "#c2410c" }}
                                  >
                                    <AlertTriangle size={18} /> Performance &
                                    Output Feedback
                                  </div>
                                  <ul
                                    className="cand-res-ai-bullets"
                                    style={{ color: "#c2410c" }}
                                  >
                                    {analysis.suggestions.map((sug, sIdx) => (
                                      <li key={sIdx}>{sug}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentCandidateResultPage;
